import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, ArrowLeft, Edit2, Save, Users, Clock, Layers } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function CsmConfigPage() {
  const [tab, setTab] = useState('queues');
  return (
    <div className="p-6 space-y-4" data-testid="csm-config-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]">CSM Configuration</h1>
          <p className="text-sm text-[#64748B] mt-1">Manage queues, assignment groups, and SLA policies.</p>
        </div>
        <Link to="/admin/csm" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to workspace
        </Link>
      </div>

      <div className="flex border-b border-[#E2E8F0]">
        {['queues', 'groups', 'sla'].map(t => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B] hover:text-[#0A2342]'}`}
                  data-testid={`tab-${t}`}>
            {t === 'queues' ? 'Queues' : t === 'groups' ? 'Assignment Groups' : 'SLA Policies'}
          </button>
        ))}
      </div>

      {tab === 'queues' && <QueuesTab />}
      {tab === 'groups' && <GroupsTab />}
      {tab === 'sla' && <SlaTab />}
    </div>
  );
}

function QueuesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/csm/queues`, { headers: authHeader() });
      setItems(r.data.queues || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Name required'); return; }
    try {
      if (editing.id) {
        await axios.patch(`${API_URL}/api/csm/queues/${editing.id}`, editing, { headers: authHeader() });
      } else {
        await axios.post(`${API_URL}/api/csm/queues`, editing, { headers: authHeader() });
      }
      toast.success('Saved');
      setEditOpen(false);
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete queue "${q.name}"? Existing tickets in this queue will keep their reference but lose the link.`)) return;
    try {
      await axios.delete(`${API_URL}/api/csm/queues/${q.id}`, { headers: authHeader() });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <Loading />;
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#64748B]">{items.length} queue{items.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => { setEditing({ name: '', description: '', default_priority: 3, active: true }); setEditOpen(true); }}
                className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="new-queue-btn">
          <Plus className="w-4 h-4 mr-2" /> New queue
        </Button>
      </div>
      <div className="space-y-2">
        {items.map(q => (
          <Card key={q.id} className="p-4 border-[#E2E8F0] flex items-center gap-3" data-testid={`queue-row-${q.id}`}>
            <Layers className="w-5 h-5 text-[#0E7490]" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#0A2342]">{q.name}</div>
              <div className="text-xs text-[#64748B]">{q.description || '—'} · Default priority: P{q.default_priority}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(q); setEditOpen(true); }} data-testid={`edit-queue-${q.id}`}>
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => remove(q)} data-testid={`delete-queue-${q.id}`}>
              <Trash2 className="w-4 h-4 text-rose-500" />
            </Button>
          </Card>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="queue-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit queue' : 'New queue'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div>
                <Label>Name</Label>
                <Input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="queue-name" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} data-testid="queue-desc" />
              </div>
              <div>
                <Label>Default priority</Label>
                <select value={editing.default_priority || 3} onChange={e => setEditing({ ...editing, default_priority: parseInt(e.target.value, 10) })}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white" data-testid="queue-prio">
                  <option value={1}>P1 — Critical</option>
                  <option value={2}>P2 — High</option>
                  <option value={3}>P3 — Moderate</option>
                  <option value={4}>P4 — Low</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} />
                Active
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="save-queue"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function GroupsTab() {
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/csm/assignment-groups`, { headers: authHeader() });
      setItems(r.data.groups || []);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/csm/agents`, { headers: authHeader() }).then(r => setAgents(r.data.agents || []));
  }, []);

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Name required'); return; }
    try {
      if (editing.id) await axios.patch(`${API_URL}/api/csm/assignment-groups/${editing.id}`, editing, { headers: authHeader() });
      else await axios.post(`${API_URL}/api/csm/assignment-groups`, editing, { headers: authHeader() });
      toast.success('Saved'); setEditOpen(false); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try { await axios.delete(`${API_URL}/api/csm/assignment-groups/${g.id}`, { headers: authHeader() }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <Loading />;
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#64748B]">{items.length} group{items.length !== 1 ? 's' : ''}</p>
        <Button onClick={() => { setEditing({ name: '', description: '', agent_ids: [], active: true }); setEditOpen(true); }}
                className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="new-group-btn">
          <Plus className="w-4 h-4 mr-2" /> New group
        </Button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <Card className="p-8 text-center text-[#64748B] border-[#E2E8F0]">No assignment groups yet.</Card>}
        {items.map(g => (
          <Card key={g.id} className="p-4 border-[#E2E8F0] flex items-center gap-3" data-testid={`group-row-${g.id}`}>
            <Users className="w-5 h-5 text-[#0E7490]" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#0A2342]">{g.name}</div>
              <div className="text-xs text-[#64748B]">{g.description || '—'} · {(g.agent_ids || []).length} agent{(g.agent_ids || []).length !== 1 ? 's' : ''}</div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(g); setEditOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => remove(g)}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
          </Card>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="group-edit-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit group' : 'New group'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div>
                <Label>Name</Label>
                <Input value={editing.name || ''} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="group-name" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={editing.description || ''} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div>
                <Label className="block mb-1">Agents</Label>
                <div className="max-h-44 overflow-y-auto border border-[#E2E8F0] rounded-md p-2 space-y-1">
                  {agents.map(a => {
                    const checked = (editing.agent_ids || []).includes(a.id);
                    return (
                      <label key={a.id} className="flex items-center gap-2 text-xs hover:bg-[#F8FAFC] rounded px-1 py-0.5">
                        <input type="checkbox" checked={checked} onChange={e => {
                          const set = new Set(editing.agent_ids || []);
                          if (e.target.checked) set.add(a.id); else set.delete(a.id);
                          setEditing({ ...editing, agent_ids: Array.from(set) });
                        }} />
                        <span className="text-[#0A2342]">{a.name}</span>
                        <span className="text-[#94A3B8]">{a.email}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="save-group"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SlaTab() {
  const [items, setItems] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API_URL}/api/csm/sla-policies`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/csm/queues`, { headers: authHeader() }),
      ]);
      setItems(a.data.policies || []);
      setQueues(b.data.queues || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await axios.post(`${API_URL}/api/csm/sla-policies`, editing, { headers: authHeader() });
      toast.success('SLA policy saved'); setEditOpen(false); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  if (loading) return <Loading />;
  const fmtMin = (m) => m >= 1440 ? `${Math.round(m / 1440)}d` : m >= 60 ? `${Math.round(m / 60)}h` : `${m}m`;
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-[#64748B]">Set response + resolution targets per priority. Queue-specific policies override globals.</p>
        <Button onClick={() => { setEditing({ queue_id: null, priority: 3, response_minutes: 240, resolution_minutes: 2880 }); setEditOpen(true); }}
                className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="new-sla-btn">
          <Plus className="w-4 h-4 mr-2" /> New / override policy
        </Button>
      </div>
      <Card className="border-[#E2E8F0] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr className="text-left text-xs font-semibold text-[#64748B] uppercase tracking-wide">
              <th className="px-4 py-2">Scope</th><th className="px-4 py-2">Priority</th><th className="px-4 py-2">Response</th><th className="px-4 py-2">Resolution</th><th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(p => (
              <tr key={p.id} className="border-b border-[#F1F5F9]" data-testid={`sla-row-${p.id}`}>
                <td className="px-4 py-2 text-xs text-[#0A2342]">{p.queue_id ? (queues.find(q => q.id === p.queue_id)?.name || p.queue_id) : <em className="text-[#64748B]">Global default</em>}</td>
                <td className="px-4 py-2 text-xs">P{p.priority}</td>
                <td className="px-4 py-2 text-xs"><Clock className="w-3 h-3 inline mr-1" /> {fmtMin(p.response_minutes)}</td>
                <td className="px-4 py-2 text-xs"><Clock className="w-3 h-3 inline mr-1" /> {fmtMin(p.resolution_minutes)}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setEditOpen(true); }}><Edit2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="sla-edit-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit policy' : 'New policy'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div>
                <Label>Scope</Label>
                <select value={editing.queue_id || ''} onChange={e => setEditing({ ...editing, queue_id: e.target.value || null })}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white" data-testid="sla-queue">
                  <option value="">Global default (all queues)</option>
                  {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Priority</Label>
                  <select value={editing.priority} onChange={e => setEditing({ ...editing, priority: parseInt(e.target.value, 10) })}
                          className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white" data-testid="sla-priority">
                    <option value={1}>P1</option><option value={2}>P2</option><option value={3}>P3</option><option value={4}>P4</option>
                  </select>
                </div>
                <div>
                  <Label>Response (minutes)</Label>
                  <Input type="number" min="1" value={editing.response_minutes} onChange={e => setEditing({ ...editing, response_minutes: parseInt(e.target.value, 10) || 0 })} data-testid="sla-resp" />
                </div>
                <div>
                  <Label>Resolution (minutes)</Label>
                  <Input type="number" min="1" value={editing.resolution_minutes} onChange={e => setEditing({ ...editing, resolution_minutes: parseInt(e.target.value, 10) || 0 })} data-testid="sla-res" />
                </div>
              </div>
              <p className="text-xs text-[#64748B]">Tip: 60 = 1h, 240 = 4h, 1440 = 1 day, 10080 = 7 days.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="save-sla"><Save className="w-4 h-4 mr-2" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Loading() {
  return <div className="text-center py-12 text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
}
